export interface SubscriptionStatus {
  status:
    "active" | "paused" | "cancelled" | "past_due" | "trialing" | "expired" | "no_subscription";
  isActive: boolean;
  canAccess: boolean;
  canCreateResidents: boolean;
  canManageSubscription: boolean;
  daysUntilExpiry: number | null;
  isInTrial: boolean;
  isPaused: boolean;
  isCancelled: boolean;
  isExpired: boolean;
  maxResidents: number | null;
  currentResidentCount?: number;
  message: string;
  nextBillingDate: string | null;
  trialEndDate: string | null;
}

export const useSubscriptionStatus = () => {
  const orgaStore = useOrgaStore();
  const organisation = computed(() => orgaStore.organisation);

  /**
   * Get comprehensive subscription status
   */
  const getSubscriptionStatus = (): SubscriptionStatus => {
    const org = organisation.value;

    if (!org) {
      return {
        status: "no_subscription",
        isActive: false,
        canAccess: false,
        canCreateResidents: false,
        canManageSubscription: false,
        daysUntilExpiry: null,
        isInTrial: false,
        isPaused: false,
        isCancelled: false,
        isExpired: false,
        maxResidents: null,
        message: "No organisation found",
        nextBillingDate: null,
        trialEndDate: null,
      };
    }

    // Check if subscription exists
    if (!org.subscription_id) {
      return {
        status: "no_subscription",
        isActive: false,
        canAccess: false,
        canCreateResidents: true,
        canManageSubscription: false,
        daysUntilExpiry: null,
        isInTrial: false,
        isPaused: false,
        isCancelled: false,
        isExpired: false,
        maxResidents: null,
        message: "No active subscription",
        nextBillingDate: null,
        trialEndDate: null,
      };
    }

    const now = new Date();
    const subscriptionStatus = org.subscription_status;
    const subscriptionEndsAt = org.subscription_ends_at;
    const trialEndsAt = org.subscription_trial_ends_at;
    const pausedAt = org.subscription_paused_at;
    const cancelledAt = org.subscription_cancelled_at;
    const resumedAt = org.subscription_resumed_at;

    // Helper function to convert date to Date object
    const toDate = (dateValue: string | object): Date | null => {
      if (!dateValue) return null;

      // Handle Luxon DateTime objects
      if (
        dateValue &&
        typeof dateValue === "object" &&
        "toISO" in dateValue &&
        typeof dateValue.toISO === "function"
      ) {
        return new Date(dateValue.toISO());
      }

      // Handle ISO string dates
      if (typeof dateValue === "string") {
        // Normalize PostgreSQL timestamp format to ISO format
        // "2025-12-26 17:04:07.632+00" -> "2025-12-26T17:04:07.632+00:00"
        // Handle both "+00" and "+00:00" formats
        let normalized = dateValue.replace(" ", "T");
        // Fix timezone format: "+00" -> "+00:00" or "-05" -> "-05:00"
        normalized = normalized.replace(
          /([+-])(\d{2})(?::(\d{2}))?$/,
          (match, sign, hours, minutes) => {
            return minutes ? match : `${sign}${hours}:00`;
          },
        );
        const date = new Date(normalized);
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          return date;
        }
        // Fallback: try parsing as-is
        const fallbackDate = new Date(dateValue);
        return !isNaN(fallbackDate.getTime()) ? fallbackDate : null;
      }

      return null;
    };

    // Check if subscription is expired
    const endsAtDate = toDate(subscriptionEndsAt);
    let isExpired = endsAtDate ? now > endsAtDate : false;
    if (subscriptionStatus === "cancelled") isExpired = false;

    // Check if subscription is in grace period (10 days after expiry)
    const gracePeriodEnd = endsAtDate
      ? new Date(endsAtDate.getTime() + 10 * 24 * 60 * 60 * 1000)
      : null;
    const isInGracePeriod =
      endsAtDate && gracePeriodEnd ? now > endsAtDate && now <= gracePeriodEnd : false;

    // Check if in trial period
    const trialEndDate = toDate(trialEndsAt);
    const isInTrial = trialEndDate ? now < trialEndDate : false;

    // Check if paused
    const pausedDate = toDate(pausedAt);
    const resumedDate = toDate(resumedAt);
    const isPaused =
      subscriptionStatus === "paused" && (!resumedDate || !pausedDate || pausedDate > resumedDate);

    // For paused subscriptions, check if we're still within the subscription period
    const isPausedWithinSubscription = isPaused && endsAtDate ? now <= endsAtDate : false;

    // Check if cancelled
    const cancelledDate = toDate(cancelledAt);
    const isCancelled = subscriptionStatus === "cancelled" || !!cancelledDate;

    // Calculate days until expiry
    const daysUntilExpiry = endsAtDate
      ? Math.ceil((endsAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null;

    // Determine final status
    let finalStatus: SubscriptionStatus["status"] = "active";
    let canAccess = true;
    let canCreateResidents = true;
    let canManageSubscription = true;
    let message = "Subscription is active";

    if (isExpired) {
      if (isInGracePeriod) {
        finalStatus = "expired";
        canAccess = true;
        canCreateResidents = true; // Allow creating residents during 10-day grace period
        canManageSubscription = true;
        message = "Subscription has expired - grace period active";
      } else {
        finalStatus = "expired";
        canAccess = true;
        canCreateResidents = false;
        canManageSubscription = false;
        message = "Subscription has expired";
      }
    } else if (isPaused) {
      finalStatus = "paused";
      canAccess = true; // Allow access to existing data
      canCreateResidents = isPausedWithinSubscription; // Allow creating residents until subscription ends
      canManageSubscription = true;
      message = isPausedWithinSubscription
        ? "Subscription is paused - can create residents until expiry"
        : "Subscription is paused";
    } else if (isCancelled) {
      finalStatus = "cancelled";
      canAccess = true; // Allow full access until expiry — backend enforces max_active_residents
      canCreateResidents = true;
      canManageSubscription = true;
      message = "Subscription is cancelled — access continues until expiry";
    } else if (subscriptionStatus === "past_due") {
      finalStatus = "past_due";
      canAccess = true; // Usually allow access during grace period
      canCreateResidents = false;
      canManageSubscription = true;
      message = "Payment is past due";
    } else if (isInTrial || subscriptionStatus === "trialing") {
      finalStatus = "trialing";
      canAccess = true;
      canCreateResidents = true;
      canManageSubscription = true;
      message = "Trial period active";
    } else if (subscriptionStatus === "active") {
      finalStatus = "active";
      canAccess = true;
      canCreateResidents = true;
      canManageSubscription = true;
      message = "Subscription is active";
    }

    // Format dates for display
    const formatDate = (dateValue: string | object): string | null => {
      if (!dateValue) return null;

      const date = toDate(dateValue);
      if (!date) return null;

      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    return {
      status: finalStatus,
      isActive: canAccess && canCreateResidents,
      canAccess,
      canCreateResidents,
      canManageSubscription,
      daysUntilExpiry,
      isInTrial,
      isPaused,
      isCancelled,
      isExpired,
      maxResidents: org.max_active_residents,
      message,
      nextBillingDate: formatDate(org.next_payment),
      trialEndDate: formatDate(trialEndsAt),
    };
  };

  /**
   * Check if user can perform a specific action
   */
  const canPerformAction = (
    action: "access" | "create_residents" | "manage_subscription",
  ): boolean => {
    const status = getSubscriptionStatus();
    // console.log("[useSubscriptionStatus] [canPerformAction] =>:", status);
    switch (action) {
      case "access":
        return status.canAccess;
      case "create_residents":
        return status.canCreateResidents;
      case "manage_subscription":
        return status.canManageSubscription;
      default:
        return false;
    }
  };

  /**
   * Get subscription status with reactive updates
   */
  const subscriptionStatus = computed(() => getSubscriptionStatus());

  /**
   * Check if subscription needs attention (past due, expiring soon, paused, cancelled, etc.)
   */
  const needsAttention = computed(() => {
    const status = subscriptionStatus.value;

    return (
      status.status === "past_due" ||
      status.status === "expired" ||
      status.status === "paused" ||
      status.status === "cancelled" ||
      (status.daysUntilExpiry !== null && status.daysUntilExpiry <= 7 && status.daysUntilExpiry > 0)
    );
  });

  /**
   * Get warning message for subscription issues
   */
  const getWarningMessage = (): string | null => {
    const status = subscriptionStatus.value;

    if (status.status === "expired") {
      if (status.canCreateResidents) {
        return "Your subscription has expired but you are in a 10-day grace period. Please renew to continue using the service after the grace period ends.";
      } else {
        return "Your subscription has expired. Please renew to continue using the service.";
      }
    }

    if (status.status === "past_due") {
      return "Your payment is past due. Please update your payment method to avoid service interruption.";
    }

    if (
      status.daysUntilExpiry !== null &&
      status.daysUntilExpiry <= 7 &&
      status.daysUntilExpiry > 0
    ) {
      return `Your subscription expires in ${status.daysUntilExpiry} day${status.daysUntilExpiry === 1 ? "" : "s"}. Please renew to avoid service interruption.`;
    }

    if (status.status === "paused") {
      if (status.canCreateResidents) {
        return "Your subscription is paused but you can still create residents until the subscription expires.";
      } else {
        return "Your subscription is paused. You can view existing data but cannot create new residents.";
      }
    }

    if (status.status === "cancelled") {
      return "Your subscription is cancelled. You can view existing data until the subscription expires.";
    }

    return null;
  };

  /**
   * Get tooltip text for create actions based on subscription status
   */
  const getCreateTooltip = (actionType: string = "create"): string => {
    const status = subscriptionStatus.value;

    if (status.status === "paused") {
      return `Your subscription is paused. Please resume your subscription to ${actionType}.`;
    } else if (status.status === "cancelled") {
      return `Your subscription is cancelled. Please upgrade your subscription to ${actionType}.`;
    } else if (status.status === "expired") {
      return `Your subscription has expired. Please renew your subscription to ${actionType}.`;
    } else if (status.status === "past_due") {
      return `Your payment is past due. Please update your payment method to ${actionType}.`;
    }

    return `${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`;
  };

  return {
    subscriptionStatus,
    getSubscriptionStatus,
    canPerformAction,
    needsAttention,
    getWarningMessage,
    getCreateTooltip,
  };
};
